// QuizAnalytics.tsx
// Detailed analytics view for a quiz

import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuizAnalytics } from '../firebase/useQuizzes';
import './QuizAnalytics.css';

export const QuizAnalytics: React.FC = () => {
    const { quizId } = useParams<{ quizId: string }>();
    const navigate = useNavigate();
    const { analytics, loading } = useQuizAnalytics(quizId || '');

    if (loading) {
        return (
            <div className="quiz-analytics">
                <div className="loading">
                    <div className="spinner"></div>
                    <p>Loading analytics...</p>
                </div>
            </div>
        );
    }

    if (!analytics) {
        return (
            <div className="quiz-analytics">
                <div className="error-message">
                    <h3>No Analytics Available</h3>
                    <p>This quiz hasn't received any responses yet.</p>
                    <button onClick={() => navigate(`/quiz-creator/quiz/${quizId}`)}>
                        Back to Quiz Details
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="quiz-analytics">
            {/* Header */}
            <header className="analytics-header">
                <button
                    className="back-button"
                    onClick={() => navigate(`/quiz-creator/quiz/${quizId}`)}
                >
                    ← Back to Quiz
                </button>
                <h1>{analytics.quizTitle}</h1>
                <p className="subtitle">Quiz Analytics Dashboard</p>
            </header>

            {/* Overview Stats */}
            <div className="analytics-overview">
                <div className="overview-card">
                    <span className="overview-icon">📊</span>
                    <div className="overview-content">
                        <span className="overview-value">{analytics.totalAttempts}</span>
                        <span className="overview-label">Total Attempts</span>
                    </div>
                </div>

                <div className="overview-card">
                    <span className="overview-icon">✅</span>
                    <div className="overview-content">
                        <span className="overview-value">{analytics.totalCompletions}</span>
                        <span className="overview-label">Completions</span>
                    </div>
                </div>

                <div className="overview-card">
                    <span className="overview-icon">📈</span>
                    <div className="overview-content">
                        <span className="overview-value">
                            {(analytics.completionRate * 100).toFixed(1)}%
                        </span>
                        <span className="overview-label">Completion Rate</span>
                    </div>
                </div>

                <div className="overview-card">
                    <span className="overview-icon">⭐</span>
                    <div className="overview-content">
                        <span className="overview-value">
                            {analytics.averageScore.toFixed(1)}%
                        </span>
                        <span className="overview-label">Average Score</span>
                    </div>
                </div>
            </div>

            {/* Score Distribution */}
            <div className="analytics-section">
                <h2>Score Statistics</h2>
                <div className="score-stats-grid">
                    <div className="score-stat">
                        <span className="score-label">Highest Score</span>
                        <span className="score-value high">
                            {analytics.highestScore.toFixed(1)}%
                        </span>
                    </div>
                    <div className="score-stat">
                        <span className="score-label">Average Score</span>
                        <span className="score-value">
                            {analytics.averageScore.toFixed(1)}%
                        </span>
                    </div>
                    <div className="score-stat">
                        <span className="score-label">Median Score</span>
                        <span className="score-value">
                            {analytics.medianScore.toFixed(1)}%
                        </span>
                    </div>
                    <div className="score-stat">
                        <span className="score-label">Lowest Score</span>
                        <span className="score-value low">
                            {analytics.lowestScore.toFixed(1)}%
                        </span>
                    </div>
                </div>
            </div>

            {/* Time Statistics */}
            <div className="analytics-section">
                <h2>Time Statistics</h2>
                <div className="time-stats-grid">
                    <div className="time-stat">
                        <span className="time-icon">⏱️</span>
                        <div className="time-content">
                            <span className="time-value">
                                {formatTime(analytics.averageTimeSpent)}
                            </span>
                            <span className="time-label">Average Time</span>
                        </div>
                    </div>
                    <div className="time-stat">
                        <span className="time-icon">🚀</span>
                        <div className="time-content">
                            <span className="time-value">
                                {formatTime(analytics.fastestCompletion)}
                            </span>
                            <span className="time-label">Fastest</span>
                        </div>
                    </div>
                    <div className="time-stat">
                        <span className="time-icon">🐌</span>
                        <div className="time-content">
                            <span className="time-value">
                                {formatTime(analytics.slowestCompletion)}
                            </span>
                            <span className="time-label">Slowest</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Question Performance */}
            <div className="analytics-section">
                <h2>Question Performance</h2>
                <div className="questions-performance">
                    {analytics.questionStats
                        .sort((a, b) => a.accuracyRate - b.accuracyRate)
                        .map((stat, index) => (
                            <div key={stat.questionId} className="question-performance-card">
                                <div className="question-performance-header">
                                    <span className="question-index">Q{index + 1}</span>
                                    <span
                                        className={`accuracy-badge ${getAccuracyClass(
                                            stat.accuracyRate
                                        )}`}
                                    >
                                        {(stat.accuracyRate * 100).toFixed(0)}% Accuracy
                                    </span>
                                </div>
                                <p className="question-performance-text">{stat.questionText}</p>
                                <div className="question-performance-stats">
                                    <div className="perf-stat">
                                        <span className="perf-stat-value">
                                            {stat.totalAttempts}
                                        </span>
                                        <span className="perf-stat-label">Attempts</span>
                                    </div>
                                    <div className="perf-stat correct">
                                        <span className="perf-stat-value">
                                            {stat.correctAttempts}
                                        </span>
                                        <span className="perf-stat-label">Correct</span>
                                    </div>
                                    <div className="perf-stat incorrect">
                                        <span className="perf-stat-value">
                                            {stat.incorrectAttempts}
                                        </span>
                                        <span className="perf-stat-label">Incorrect</span>
                                    </div>
                                </div>
                                <div className="accuracy-bar">
                                    <div
                                        className={`accuracy-fill ${getAccuracyClass(
                                            stat.accuracyRate
                                        )}`}
                                        style={{ width: `${stat.accuracyRate * 100}%` }}
                                    />
                                </div>
                            </div>
                        ))}
                </div>
            </div>

            {/* Recent Responses */}
            {analytics.recentResponses.length > 0 && (
                <div className="analytics-section">
                    <h2>Recent Responses</h2>
                    <div className="recent-responses-table">
                        <table>
                            <thead>
                                <tr>
                                    <th>User</th>
                                    <th>Score</th>
                                    <th>Correct</th>
                                    <th>Time</th>
                                    <th>Date</th>
                                </tr>
                            </thead>
                            <tbody>
                                {analytics.recentResponses.map((response) => (
                                    <tr key={response.id}>
                                        <td>{response.responderUsername || 'Anonymous'}</td>
                                        <td>
                                            <span
                                                className={`score-badge ${getScoreClass(
                                                    response.percentageScore
                                                )}`}
                                            >
                                                {response.percentageScore.toFixed(1)}%
                                            </span>
                                        </td>
                                        <td>
                                            {response.correctAnswers}/{response.totalQuestions}
                                        </td>
                                        <td>{formatTime(response.timeSpent)}</td>
                                        <td>
                                            {new Date(response.completedAt).toLocaleDateString()}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Export Button */}
            <div className="analytics-actions">
                <button className="export-button" onClick={() => window.print()}>
                    📄 Export Report
                </button>
            </div>
        </div>
    );
};

// ============================================
// HELPER FUNCTIONS
// ============================================

const formatTime = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
};

const getAccuracyClass = (accuracy: number): string => {
    if (accuracy >= 0.8) return 'high';
    if (accuracy >= 0.5) return 'medium';
    return 'low';
};

const getScoreClass = (score: number): string => {
    if (score >= 80) return 'high';
    if (score >= 60) return 'medium';
    return 'low';
};
